import { useQuery } from '@tanstack/react-query'
import { useContext, useEffect, useState } from 'react'
import styled from 'styled-components'

import { BanksArr, MODALS_ID, QUERY_KEY } from '../../constants'
import type { BankNames } from '../../constants'

import { queryClient } from '../../utils/queryClient'

import { StoresContext } from '../../provider/StoreProvider'
import { ModalContext } from '../../provider/ModalProvider'

import { useNavigate } from '../../hooks/useNavigate'
import { useCheckPrevPathname } from '../../hooks/useCheckPrevPathname'
import { useUserInfo } from '../../hooks/useUserInfo'

import { getUnmaskedWithdrawalAccount } from '../../api/withdrawalAccount'

import { Wrap } from '../../components/container/Wrap'
import { Content } from '../../components/container/Content'
import { BankIcon } from '../../components/bankIcon'
import { Button } from '../../components/button'
import { SecurityIcon } from '../../components/securityIcon'
import { Text } from '../../components/text'
import type { BankName } from '../../components/bankIcon/BankIcon'
import type { SecurityName } from '../../components/securityIcon/SecurityIcon'
import { Footer } from '../../components/footer'
import { Header } from '../../components/header'
import { NoticeContainer } from '../../components/noticeContainer/NoticeContainer'
import { SuccessChangedWithdrawalModal } from './modals/SuccessChangedWithdrawalModal'

import { Color } from '../../theme/color'

// *출금 계좌 등록 성공 페이지*
// 로그인, 본인인증 유무 상관 없이 [출금 계좌 선택 페이지]에서 온 게 아니라면 무조건 접근 불가
export function SuccessWithdrawal() {
  const {
    commonStore: { setShowTab, setInvalidAccess },
  } = useContext(StoresContext)
  const { openModal } = useContext(ModalContext)

  const [isDeposit, setIsDeposit] = useState<boolean>(false)

  const isValidPrevPathname = useCheckPrevPathname('/account/withdrawal/banks')
  const navigate = useNavigate()
  const { data: userInfo, isLogin, isCheckingLogin } = useUserInfo()

  const {
    data: unmaskedWithdrawalAccount,
    isPending,
    isError,
  } = useQuery({
    queryKey: [QUERY_KEY.withdrawalAccount, QUERY_KEY.unmasked],
    queryFn: getUnmaskedWithdrawalAccount,
  })

  useEffect(() => {
    if (!isLogin && !isCheckingLogin) setInvalidAccess(true)
  }, [isLogin, isCheckingLogin])

  useEffect(() => {
    if (isValidPrevPathname) {
      setInvalidAccess(true)
    }
  }, [isValidPrevPathname])

  useEffect(() => {
    setShowTab(false)
    queryClient.invalidateQueries({ queryKey: [QUERY_KEY.userInfo] })

    // 출금 계좌 등록인지 변경인지 상태 체크
    const isUpdate = Boolean(window.location.search.split('=').pop())
    if (isUpdate) {
      openModal({ id: MODALS_ID.SUCCESS_CHANGED_WITHDRAWAL, Component: SuccessChangedWithdrawalModal })
    }
  }, [])

  useEffect(() => {
    setIsDeposit(!!userInfo?.depositAccount)
  }, [userInfo])

  if (isPending || isError) {
    return null
  }

  return (
    <Wrap>
      <Header type="SUB" title="출금 계좌" />
      <Content backgroundColor="gray_1">
        <BankContainer>
          <Text size={24} weight={600} lineHeight={34}>
            출금 계좌 정보
          </Text>
          <RowBetween>
            <BankInfo>
              {BanksArr.includes(unmaskedWithdrawalAccount.bankName as BankNames) ? (
                <BankIcon size={46} name={unmaskedWithdrawalAccount.bankName as BankName} />
              ) : (
                <SecurityIcon size={46} name={unmaskedWithdrawalAccount.bankName as SecurityName} />
              )}
              <Text size={18} weight={600}>
                {unmaskedWithdrawalAccount.bankName}
              </Text>
            </BankInfo>
          </RowBetween>
          <RowBetween>
            <Text size={14} weight={600} color="text_secondary">
              계좌번호
            </Text>
            <Text size={14} color="text_secondary">
              {unmaskedWithdrawalAccount.account}
            </Text>
          </RowBetween>
          <RowBetween>
            <Text size={14} weight={600} color="text_secondary">
              예금주
            </Text>
            <Text size={14} color="text_secondary">
              {unmaskedWithdrawalAccount.bankOwner}
            </Text>
          </RowBetween>
        </BankContainer>
        <NoticeContainer
          title="안내사항"
          list={[
            '출금계좌는 서비스 가입자 본인 명의의 실계좌만 가능해요.',
            '출금계좌등록 및 변경은 더보기>개인정보관리>출금계좌에서 가능해요.',
            <Text size={14} lineHeight={22} color="error">
              NH농협은행 예치금 계좌로의 입금은 등록된 출금계좌로만 가능해요.
            </Text>,
          ]}
          $backgroundColor="gray_1"
        />
      </Content>
      <Footer>
        <Button
          type="primary"
          size="large"
          block={true}
          {...(isDeposit
            ? { onClick: () => navigate(-1) }
            : { href: '/account/deposit/terms', replace: true, state: { prevPathname: window.location.pathname } })}
        >
          {isDeposit ? '변경하기' : '다음'}
        </Button>
      </Footer>
    </Wrap>
  )
}

const BankContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 56px 24px 40px;
  background-color: ${Color.white};
  > *:first-child {
    margin-bottom: 8px;
  }
`

const RowBetween = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const BankInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 8px;
`
